// Full Stock Take Count Page - Count All Items
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Search,
  Package,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { format } from "date-fns";
import { generateMockStockItems } from "@/data/mockStockItems";

interface CountItem {
  code: string;
  description: string;
  category: string;
  location: string;
  systemQty: number;
  countedQty: string;
  variance: number;
  notes: string;
  isCounted: boolean;
  warehouseCode: string;
}

interface StockTakesFullCountPageProps {
  setupData?: {
    description: string;
    date: Date;
    warehouse: string;
    notes: string;
    assignedTo: string;
    type: string;
  };
}

export function StockTakesFullCountPage({ setupData }: StockTakesFullCountPageProps) {
  const { navigateTo } = useNavigation();
  
  // Initialize count items from mock stock items, filtered by warehouse
  const [countItems, setCountItems] = useState<CountItem[]>(() => {
    const stockItems = generateMockStockItems();
    
    // Filter by warehouse if specified
    const filteredItems = setupData?.warehouse && setupData.warehouse !== "all"
      ? stockItems.filter(item => item.warehouseCode === setupData.warehouse)
      : stockItems;
    
    return filteredItems.map(item => ({
      code: item.code,
      description: item.description,
      category: item.category,
      location: `${item.warehouseCode} - ${item.locationCode}`,
      systemQty: item.physicalQuantity,
      countedQty: "",
      variance: 0,
      notes: "",
      isCounted: false,
      warehouseCode: item.warehouseCode,
    }));
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showOnlyVariances, setShowOnlyVariances] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const updateCountItem = (code: string, field: keyof CountItem, value: string | boolean) => {
    setCountItems(prev =>
      prev.map(item => {
        if (item.code === code) {
          const updated = { ...item, [field]: value };
          
          // Auto-mark as counted when quantity is entered
          if (field === "countedQty" && value !== "") {
            updated.isCounted = true;
            // Calculate variance when counted quantity changes
            const counted = parseFloat(value as string) || 0;
            updated.variance = counted - item.systemQty;
          }
          
          return updated;
        }
        return item;
      })
    );
  };

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(countItems.map(item => item.category));
    return Array.from(cats).sort();
  }, [countItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = countItems;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Status filter
    if (statusFilter === "counted") {
      filtered = filtered.filter(item => item.isCounted);
    } else if (statusFilter === "not-counted") {
      filtered = filtered.filter(item => !item.isCounted);
    }

    // Show only variances
    if (showOnlyVariances) {
      filtered = filtered.filter(item => item.variance !== 0);
    }

    return filtered;
  }, [countItems, searchQuery, categoryFilter, statusFilter, showOnlyVariances]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, statusFilter, showOnlyVariances, itemsPerPage]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = countItems.length;
    const counted = countItems.filter(item => item.isCounted).length;
    const remaining = total - counted;
    const variances = countItems.filter(item => item.variance !== 0).length;
    const increases = countItems.filter(item => item.variance > 0).length;
    const decreases = countItems.filter(item => item.variance < 0).length;
    const totalVarianceQty = countItems.reduce((sum, item) => sum + Math.abs(item.variance), 0);

    return {
      total,
      counted,
      remaining,
      variances,
      increases,
      decreases,
      totalVarianceQty,
    };
  }, [countItems]);

  const handleSaveDraft = () => {
    const draftData = {
      setupData,
      countItems,
      savedAt: new Date().toISOString(),
    };
    console.log("Saving draft:", draftData);
    // TODO: Save to storage
    alert("Draft saved successfully!");
  };

  const handleContinueToReview = () => {
    navigateTo("stock-takes/full/review", {
      setupData,
      countedItems: countItems,
    });
  };

  const getVarianceIcon = (variance: number) => {
    if (variance > 0) return <TrendingUp className="h-4 w-4" />;
    if (variance < 0) return <TrendingDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };

  const getVarianceBadge = (variance: number) => {
    if (variance > 0) {
      return (
        <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          <TrendingUp className="h-3 w-3" />
          +{variance}
        </Badge>
      );
    }
    if (variance < 0) {
      return (
        <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <TrendingDown className="h-3 w-3" />
          {variance}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <Minus className="h-3 w-3" />
        0
      </Badge>
    );
  };

  const getStatusBadge = (isCounted: boolean) => {
    if (isCounted) {
      return (
        <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle2 className="h-3 w-3" />
          Counted
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
        <AlertCircle className="h-3 w-3" />
        Pending
      </Badge>
    );
  };

  // Pagination controls
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push(-1); // ellipsis
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push(-1);
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push(-1);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push(-1);
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateTo("stock-takes/full/setup")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Setup
        </Button>
        <Button variant="outline" size="sm" onClick={handleSaveDraft} className="gap-2">
          <Save className="h-4 w-4" />
          Save Draft
        </Button>
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Full Stock Take</h1>
              <Badge variant="secondary">Step 2 of 3</Badge>
            </div>
            <p className="text-muted-foreground">Count physical stock quantities</p>
          </div>
        </div>
      </div>

      {/* Setup Summary */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Count Date</p>
              <p className="font-medium">
                {setupData?.date ? format(new Date(setupData.date), "MMMM do, yyyy") : "Not set"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Warehouse</p>
              <p className="font-medium">{setupData?.warehouse === "all" ? "All Warehouses" : setupData?.warehouse || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Assigned To</p>
              <p className="font-medium">{setupData?.assignedTo || "Unassigned"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="font-medium">{setupData?.description || "No description"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Items</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Counted</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.counted}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Remaining</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.remaining}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Variances</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.variances}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="counted">Counted</SelectItem>
                <SelectItem value="not-counted">Not Counted</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Checkbox
                id="show-variances"
                checked={showOnlyVariances}
                onCheckedChange={(checked) => setShowOnlyVariances(checked as boolean)}
              />
              <label
                htmlFor="show-variances"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Show only variances
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Table */}
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Stock Items</CardTitle>
              <CardDescription>Enter physical count for each item</CardDescription>
            </div>
            <p className="text-sm text-muted-foreground">
              {startIndex + 1} to {Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
            </p>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Code</TableHead>
                  <TableHead className="min-w-[200px]">Description</TableHead>
                  <TableHead className="w-[120px]">Category</TableHead>
                  <TableHead className="w-[120px]">Location</TableHead>
                  <TableHead className="w-[100px] text-right">System Qty</TableHead>
                  <TableHead className="w-[120px] text-right">Counted Qty</TableHead>
                  <TableHead className="w-[100px]">Variance</TableHead>
                  <TableHead className="min-w-[200px]">Notes</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedItems.map((item) => (
                  <TableRow key={item.code}>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.category}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{item.location}</TableCell>
                    <TableCell className="text-right font-medium">{item.systemQty}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={item.countedQty}
                        onChange={(e) => updateCountItem(item.code, "countedQty", e.target.value)}
                        placeholder="0"
                        className="w-24 text-right ml-auto"
                      />
                    </TableCell>
                    <TableCell>{getVarianceBadge(item.variance)}</TableCell>
                    <TableCell>
                      <Textarea
                        value={item.notes}
                        onChange={(e) => updateCountItem(item.code, "notes", e.target.value)}
                        placeholder="Optional notes"
                        className="min-h-[60px] resize-none"
                        rows={2}
                      />
                    </TableCell>
                    <TableCell>{getStatusBadge(item.isCounted)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => setItemsPerPage(Number(value))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  page === -1 ? (
                    <span key={`ellipsis-${index}`} className="px-2">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-10"
                    >
                      {page}
                    </Button>
                  )
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes/full/setup")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Setup
        </Button>
        <Button onClick={handleContinueToReview} className="gap-2">
          Continue to Review
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
